import { SYSTEM_ACTOR_ID, type AuditLog, type AuditLogActor } from '@bliks-io/contracts';
import { Badge, createColumnHeader, createDateColumn, type DataTableColumnDef } from '@bliks-io/ui';
import { ACTION_VARIANTS, getActionLabel, getEntityTypeLabel } from './auditLogs.constants';
import type { TFn } from '@/lib/i18n';

interface CreateAuditLogColumnsArgs {
  t: TFn;
  actors: Record<string, AuditLogActor> | undefined;
  actionOptions: { label: string; value: string }[];
  entityTypeOptions: { label: string; value: string }[];
  setColumnFilter: (columnId: string, value: string) => void;
  onSelect: (log: AuditLog) => void;
}

export function createAuditLogColumns({
  t,
  actors,
  actionOptions,
  entityTypeOptions,
  setColumnFilter,
  onSelect
}: CreateAuditLogColumnsArgs): DataTableColumnDef<AuditLog>[] {
  return [
    {
      ...createDateColumn<AuditLog>('timestamp', t('auditLogs.timestamp'), {
        format: 'datetime',
        enableFiltering: true
      }),
      // Server-paginated data — disable client-side filtering
      filterFn: () => true
    },
    {
      accessorKey: 'actorId',
      meta: { label: t('auditLogs.actor'), filterType: 'text' },
      header: createColumnHeader(t('auditLogs.actor')),
      cell: ({ row }) => {
        const actorId = row.getValue<string>('actorId');
        if (actorId === SYSTEM_ACTOR_ID) {
          return <span className="text-xs italic">{t('auditLogs.systemActor')}</span>;
        }
        const actor = actors?.[actorId];
        const label = actor ? `${actor.firstName} ${actor.lastName}` : actorId;
        return (
          <button
            type="button"
            className="text-xs underline-offset-2 hover:underline cursor-pointer text-left"
            title={t('auditLogs.filterByThisActor')}
            onClick={(e) => {
              e.stopPropagation();
              setColumnFilter('actorId', actorId);
            }}
          >
            {actor ? (
              <span title={actor.email}>{label}</span>
            ) : (
              <span className="font-mono">{label}</span>
            )}
          </button>
        );
      },
      enableColumnFilter: true,
      filterFn: () => true
    },
    {
      accessorKey: 'action',
      meta: {
        label: t('auditLogs.action'),
        filterType: 'select',
        filterOptions: actionOptions
      },
      header: createColumnHeader(t('auditLogs.action')),
      cell: ({ row }) => {
        const action = row.getValue<string>('action');
        return (
          <button
            type="button"
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(row.original);
            }}
          >
            <Badge variant={ACTION_VARIANTS[action] ?? 'outline'}>
              {getActionLabel(t, action)}
            </Badge>
          </button>
        );
      },
      enableColumnFilter: true,
      filterFn: () => true
    },
    {
      accessorKey: 'entityType',
      meta: {
        label: t('auditLogs.entityType'),
        filterType: 'select',
        filterOptions: entityTypeOptions
      },
      header: createColumnHeader(t('auditLogs.entityType')),
      cell: ({ row }) => {
        const entityType = row.getValue<string>('entityType');
        return (
          <button
            type="button"
            className="text-xs underline-offset-2 hover:underline cursor-pointer text-left"
            title={t('auditLogs.filterByThisEntity')}
            onClick={(e) => {
              e.stopPropagation();
              setColumnFilter('entityType', entityType);
            }}
          >
            {getEntityTypeLabel(t, entityType)}
          </button>
        );
      },
      enableColumnFilter: true,
      filterFn: () => true
    },
    {
      accessorKey: 'entityId',
      meta: { label: t('auditLogs.entityId'), filterType: 'text' },
      header: createColumnHeader(t('auditLogs.entityId')),
      cell: ({ row }) => {
        const id = row.getValue<string | null>('entityId');
        if (!id) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <button
            type="button"
            className="font-mono text-xs underline-offset-2 hover:underline cursor-pointer text-left"
            title={t('auditLogs.filterByThisEntityId')}
            onClick={(e) => {
              e.stopPropagation();
              setColumnFilter('entityId', id);
            }}
          >
            {id}
          </button>
        );
      },
      enableColumnFilter: true,
      filterFn: () => true
    },
    {
      id: 'summary',
      header: t('auditLogs.summary'),
      cell: ({ row }) => {
        const log = row.original;
        if (log.changes && log.changes.length > 0) {
          return (
            <span className="text-xs">
              {t('auditLogs.fieldsChanged', { count: log.changes.length })}
            </span>
          );
        }
        if (log.batchInfo) {
          return (
            <span className="text-xs">
              {t('auditLogs.matchedModified', {
                matched: log.batchInfo.matchedCount,
                modified: log.batchInfo.modifiedCount
              })}
            </span>
          );
        }
        if (log.snapshot) {
          return <span className="text-xs">{t('auditLogs.snapshotCaptured')}</span>;
        }
        return null;
      }
    }
  ];
}
